import { getCallDataForUserop } from '../lib/getCallDataForUserOp';
import { needsUpgrade } from '../utils/needsUpgrade';
import { totalCostOfUserOp } from '../lib/permissionless/middleware/balance';
import { selectUserOpsByAddress } from '../store/userOpsStore';
import { getAction } from 'viem/utils';
import { getBalance, readContract } from 'viem/actions';
import { decodeCallData } from '../utils/decodeCallData';
import { InsufficientTipBalanceException } from '../errors';
import { prepareUnsponsoredOp } from '../utils/prepareUnsponsoredOp';
import { erc20Abi, ethAddress, zeroAddress } from 'viem';
import { encodeERC20Approval } from '../utils/encodeERC20Approval';
import { parseUnits } from 'viem';
export async function tip(params) {
    const { spaceDapp, fnArgs, newAccountImplementationType, smartAccount } = params;
    const { tipParams, signer } = fnArgs;
    const { amount, type } = tipParams;
    const space = (() => {
        if (tipParams.type === 'any')
            return;
        if (!tipParams.spaceId)
            return;
        const space = spaceDapp?.getSpace(tipParams.spaceId);
        if (!space) {
            throw new Error(`Space with spaceId "${tipParams.spaceId}" is not found.`);
        }
        return space;
    })();
    const accountModulesAddress = spaceDapp?.config.addresses.accountModules;
    const account = smartAccount.client.account;
    const isDmTip = type === 'any' || (type === 'bot' && !tipParams.spaceId);
    if (!account) {
        throw new Error('smart account undefined');
    }
    if (isDmTip && !accountModulesAddress) {
        throw new Error('Account module is not deployed for this environment');
    }
    // Encode the sendTip function call with recipientType and encodedData
    const encoded = await spaceDapp?.sendTip({
        tipParams,
        signer,
        overrideExecution: async ({ calldata }) => ({ calldata }),
    });
    if (!encoded) {
        throw new Error('Failed to encode sendTip function call');
    }
    const { calldata: sendTipCallData } = encoded;
    // For USDC tips, add approval for the Space contract to spend the tip amount
    const { currency } = tipParams;
    const isErc20Tip = currency.toLowerCase() !== ethAddress.toLowerCase() &&
        currency.toLowerCase() !== zeroAddress.toLowerCase();
    let usdcApprovalCallData;
    const finalToAddress = isDmTip
        ? [accountModulesAddress]
        : [space.Address];
    const finalCallData = [sendTipCallData];
    // For USDC, value should be 0 (ERC-20 doesn't use value parameter)
    // For ETH, value is the tip amount
    let finalValue = [amount];
    if (isErc20Tip) {
        // For ERC20 tips, add approval and set value to 0
        usdcApprovalCallData = encodeERC20Approval({
            tokenAddress: currency,
            spender: isDmTip ? accountModulesAddress : space.Address,
            amount,
        });
        finalToAddress.unshift(currency);
        finalCallData.unshift(usdcApprovalCallData);
        finalValue = [0n, 0n];
    }
    // Check the sender has enough balance, otherwise throw an error before trying to send the user op
    if (isErc20Tip) {
        // For USDC tips: check USDC balance covers tip amount
        // Gas will be paid via sponsorOrUSDC middleware
        const usdcBalance = await getAction(smartAccount.publicRpcClient, readContract, 'readContract')({
            address: currency,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [smartAccount.address],
        });
        // TODO: should call tokenQuote directly here to get gas cost and compare to balance? but will have to deal with the same issues that the worker does w/ alchemy's tokenQuote endopint - unreliable responses from alchemy
        // let's just add 2 cent buffer, hopefully that's fine
        if (usdcBalance < amount + parseUnits('0.02', 6)) {
            throw new InsufficientTipBalanceException();
        }
    }
    else {
        const userOpCallData = await getCallDataForUserop({
            toAddress: finalToAddress,
            callData: finalCallData,
            value: finalValue,
            smartAccount,
            signer,
            needsUpgrade: needsUpgrade(newAccountImplementationType, smartAccount.type),
            functionHash: 'tip',
        });
        const preparedUserOp = await prepareUnsponsoredOp({
            smartAccount,
            callData: userOpCallData,
        });
        const { functionHashForPaymasterProxy } = selectUserOpsByAddress(account.address).current;
        const valueBigInt = preparedUserOp.callData
            ? decodeCallData({
                callData: preparedUserOp.callData,
                functionHash: functionHashForPaymasterProxy,
            })?.value
            : undefined;
        const totalCost = totalCostOfUserOp({
            gasLimit: preparedUserOp.callGasLimit,
            preVerificationGas: preparedUserOp.preVerificationGas,
            verificationGasLimit: preparedUserOp.verificationGasLimit,
            gasPrice: preparedUserOp.maxFeePerGas,
            value: valueBigInt,
        });
        // For ETH tips: check native balance covers tip + gas
        const balance = await getAction(smartAccount.publicRpcClient, getBalance, 'getBalance')({
            address: preparedUserOp.sender,
        });
        if (balance < totalCost) {
            throw new InsufficientTipBalanceException();
        }
    }
    return smartAccount.sendUserOp({
        toAddress: finalToAddress,
        callData: finalCallData,
        skipConfirmation: true,
        signer,
        spaceId: tipParams.type !== 'any' ? tipParams.spaceId : undefined,
        value: finalValue,
        functionHashForPaymasterProxy: 'tip',
    });
}
